// src/services/venuesService.js
// CRUD venues + helpers de linkado con events (event_venues).
// Objetivo: encapsular Supabase y mantener la lógica de venue/event_venues en un único sitio.

import { getSupabaseClient, normalizeSupabaseError } from "./supabaseClient.js";
import { TABLES } from "./eventsService.js";
import { getKmFromOrigin } from "./geocodingService.js";

// =========================
// Helpers internos
// =========================
function assertId(id, name = "id") {
  if (id === null || id === undefined || String(id).trim() === "") {
    throw new Error(`${name} es obligatorio.`);
  }
}

function pickColumns(obj, whitelist) {
  if (!whitelist) return { ...obj };
  const out = {};
  for (const k of whitelist) {
    if (Object.prototype.hasOwnProperty.call(obj, k)) out[k] = obj[k];
  }
  return out;
}

function throwSupabase(error, context = "") {
  if (!error) return;
  const n = normalizeSupabaseError(error);
  const msg = context ? `${context}: ${n.message}` : n.message;
  const e = new Error(msg);
  e.code = n.code;
  e.details = n.details;
  e.hint = n.hint;
  e.raw = n.raw;
  throw e;
}

// =========================
// VENUES: lecturas
// =========================

/**
 * Lista venues por team_id.
 */
export async function listVenuesByTeam(
  teamId,
  { orderBy = "name", ascending = true, select = "*" } = {}
) {
  assertId(teamId, "teamId");
  const supabase = getSupabaseClient();

  const { data, error } = await supabase
    .from(TABLES.VENUES)
    .select(select)
    .eq("team_id", teamId)
    .order(orderBy, { ascending });

  throwSupabase(error, "Error listando venues");
  return data || [];
}

/**
 * Obtiene venue por id.
 */
export async function getVenueById(venueId, { select = "*" } = {}) {
  assertId(venueId, "venueId");
  const supabase = getSupabaseClient();

  const { data, error } = await supabase.from(TABLES.VENUES).select(select).eq("id", venueId).single();
  throwSupabase(error, "Error leyendo venue");
  return data;
}

/**
 * Búsqueda simple (ILIKE) por nombre/dirección.
 */
export async function searchVenues(teamId, query, { limit = 25, select = "*" } = {}) {
  assertId(teamId, "teamId");
  const q = String(query || "").trim();
  if (!q) return [];

  const supabase = getSupabaseClient();

  // OR: name ilike, address ilike
  const or = [`name.ilike.%${q}%`, `address.ilike.%${q}%`].join(",");

  const { data, error } = await supabase
    .from(TABLES.VENUES)
    .select(select)
    .eq("team_id", teamId)
    .or(or)
    .limit(limit);

  throwSupabase(error, "Error buscando venues");
  return data || [];
}

// =========================
// VENUES: escrituras
// =========================


/**
 * Crea venue.
 * options:
 * - whitelist: string[] (defecto: columnas validas DB)
 * - select: string
 */
export async function createVenue(payload, { whitelist = null, select = "*" } = {}) {
  if (!payload || typeof payload !== "object") throw new Error("payload inválido.");
  
  // Whitelist por defecto para evitar errores de columnas inexistentes
  const defaultWhitelist = ["team_id", "name", "region", "city", "address", "contact_notes", "ops_notes", "pricing_notes", "lat", "lng", "km_from_origin"];
  const distinctWhitelist = whitelist || defaultWhitelist;

  const supabase = getSupabaseClient();
  const insertPayload = pickColumns(payload, distinctWhitelist);

  const { data, error } = await supabase.from(TABLES.VENUES).insert(insertPayload).select(select).single();
  throwSupabase(error, "Error creando venue");
  return data;
}


/**
 * Actualiza venue.
 */
export async function updateVenue(venueId, patch, { whitelist = null } = {}) {
  assertId(venueId, "venueId");
  if (!patch || typeof patch !== "object") throw new Error("patch inválido.");
  
  // Whitelist por defecto
  const defaultWhitelist = ["team_id", "name", "region", "city", "address", "contact_notes", "ops_notes", "pricing_notes", "lat", "lng", "km_from_origin"];
  const distinctWhitelist = whitelist || defaultWhitelist;

  const supabase = getSupabaseClient();
  const updatePayload = pickColumns(patch, distinctWhitelist);

  const { error } = await supabase.from(TABLES.VENUES).update(updatePayload).eq("id", venueId);
  throwSupabase(error, "Error actualizando venue");
  return true;
}

/**
 * Borra venue.
 */
export async function deleteVenue(venueId) {
  assertId(venueId, "venueId");
  const supabase = getSupabaseClient();

  const { error } = await supabase.from(TABLES.VENUES).delete().eq("id", venueId);
  throwSupabase(error, "Error borrando venue");
  return true;
}

/**
 * Si el venue no tiene km_from_origin cacheado, geocodifica su direccion
 * (o city/region como fallback) y calcula/persiste lat, lng y
 * km_from_origin. Si ya lo tiene, o si el calculo falla (direccion no
 * geocodificable, servicio caido), devuelve el venue tal cual sin lanzar.
 *
 * @param {object} venue
 * @returns {Promise<object>} el venue, actualizado si hizo falta
 */
export async function ensureVenueDistance(venue) {
  if (!venue || venue.km_from_origin !== null && venue.km_from_origin !== undefined) return venue;

  const query = [venue.address || `${venue.city || ""} ${venue.region || ""}`.trim(), "España"]
    .filter(Boolean)
    .join(", ");
  if (!query.trim()) return venue;

  try {
    const result = await getKmFromOrigin(query);
    if (!result) return venue;

    await updateVenue(venue.id, {
      lat: result.lat,
      lng: result.lng,
      km_from_origin: result.kmFromOrigin,
    });

    return { ...venue, lat: result.lat, lng: result.lng, km_from_origin: result.kmFromOrigin };
  } catch (e) {
    return venue;
  }
}

// =========================
// EVENT_VENUES (linkado)
// =========================

/**
 * Crea vínculo event_venue.
 * payload típico:
 * { team_id, event_id, venue_id, is_primary }
 */
export async function linkVenueToEvent(payload, { select = "*" } = {}) {
  if (!payload || typeof payload !== "object") throw new Error("payload inválido.");
  if (!payload.team_id) throw new Error("payload.team_id obligatorio.");
  if (!payload.event_id) throw new Error("payload.event_id obligatorio.");
  if (!payload.venue_id) throw new Error("payload.venue_id obligatorio.");

  const supabase = getSupabaseClient();

  const { data, error } = await supabase
    .from(TABLES.EVENT_VENUES)
    .insert(payload)
    .select(select)
    .single();

  throwSupabase(error, "Error linkando venue a event");
  return data;
}

/**
 * Borra vínculo event_venues por id (fila link).
 */
export async function unlinkEventVenue(linkId) {
  assertId(linkId, "linkId");
  const supabase = getSupabaseClient();

  const { error } = await supabase.from(TABLES.EVENT_VENUES).delete().eq("id", linkId);
  throwSupabase(error, "Error borrando vínculo event_venues");
  return true;
}

/**
 * Borra vínculo por event_id + venue_id.
 */
export async function unlinkVenueFromEvent(eventId, venueId) {
  assertId(eventId, "eventId");
  assertId(venueId, "venueId");
  const supabase = getSupabaseClient();

  const { error } = await supabase
    .from(TABLES.EVENT_VENUES)
    .delete()
    .eq("event_id", eventId)
    .eq("venue_id", venueId);

  throwSupabase(error, "Error borrando vínculo venue->event");
  return true;
}

/**
 * Devuelve el venue primary de un evento (con join a venues) o null.
 */
export async function getPrimaryVenueForEvent(eventId) {
  assertId(eventId, "eventId");
  const supabase = getSupabaseClient();

  const { data, error } = await supabase
    .from(TABLES.EVENT_VENUES)
    .select("id, venue_id, is_primary, venues:venue_id (id, name, address, region, city)")
    .eq("event_id", eventId)
    .eq("is_primary", true)
    .maybeSingle();

  throwSupabase(error, "Error leyendo venue primary del evento");
  if (!data) return null;

  return {
    link: {
      id: data.id,
      venue_id: data.venue_id,
      is_primary: data.is_primary,
    },
    venue: data.venues || null,
  };
}

/**
 * Establece un venue como primary para un evento.
 * Delegamos en eventsService para mantener lógica única.
 */
export async function setPrimaryVenueForEvent(teamId, eventId, venueId) {
  const mod = await import("./eventsService.js");
  return mod.upsertPrimaryEventVenue(teamId, eventId, venueId);
}
